<template>
  <div>
    <ThemeECommerceMenuBreadcrumb
      v-if="isECommerce"
      :items="breadcrumbs"
    />
    <ThemeWebSphereMenuBreadcrumb
      v-else-if="isWebSphere"
      :items="breadcrumbs"
    />
    <ThemeIndustrialServiceDisplayBreadcrumbSection
      v-else-if="isIndustrial"
      :items="breadcrumbs"
    />
    <ThemeDefaultMenuBreadcrumb
      v-else
      :items="breadcrumbs"
    />

    <main
      class="tw-container"
      :class="mainClass"
    >
      <h2
        v-if="isIndustrial"
        class="tw-mb-4 tw-text-mb-title-1 tw-font-medium tw-text-black lg:tw-hidden"
      >
        {{ $t('global.writer') }}
      </h2>

      <section
        class="tw-flex"
        :class="profileSectionClass"
      >
        <img
          v-if="authorImage && !imageLoadFailed"
          :src="authorImage"
          :alt="authorName"
          class="tw-aspect-square tw-rounded-full tw-object-cover"
          :class="authorAvatarClass"
          loading="eager"
          decoding="async"
          @error="imageLoadFailed = true"
        >
        <div
          v-else
          class="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-light-gray-1 tw-text-mb-title-1 tw-text-dark-gray"
          :class="authorAvatarClass"
        >
          {{ initial }}
        </div>

        <div
          class="tw-w-full"
          :class="profileTextClass"
        >
          <h1
            class="author-text tw-text-black"
            :class="authorNameClass"
          >
            {{ authorName }}
          </h1>
          <p
            v-if="author?.bio"
            class="author-text tw-text-dark-gray"
            :class="authorBioClass"
          >
            {{ author.bio }}
          </p>

          <div
            v-if="socialLinks.length > 0"
            class="tw-flex tw-items-center"
            :class="socialListClass"
          >
            <a
              v-for="item in socialLinks"
              :key="item.key"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-flex tw-items-center tw-justify-center tw-text-dark-gray tw-transition-colors hover:tw-text-primary"
              :class="socialIconClass"
            >
              <QIcon :name="item.icon" :size="socialIconSize" />
            </a>
          </div>
        </div>
      </section>

      <section :class="articleSectionClass">
        <div
          v-if="articles.length > 0"
          :class="articleListClass"
        >
          <article
            v-for="item in articles"
            :key="`author-article-${item._id}`"
            class="tw-overflow-hidden tw-bg-white"
            :class="articleCardClass"
          >
            <NuxtLink
              :to="getPath({ componentName: 'Article', slug: item.slug })"
              class="tw-grid tw-min-w-0 tw-grid-cols-1"
              :class="articleLinkClass"
            >
              <MediaImageLazyLoad
                :src="item.imageCover?.medium || item.imageCover?.large || ''"
                :placeholder-src="item.imageCover?.tiny || ''"
                :alt="item.title"
                class="tw-h-full tw-w-full"
                :custom-class="articleImageClass"
                seo-optimize
              />
              <div
                class="tw-flex tw-min-w-0 tw-flex-col tw-justify-center"
                :class="articleContentClass"
              >
                <div
                  v-if="!isWebSphere && !isIndustrial"
                  class="tw-flex tw-items-center tw-gap-2 tw-text-mb-body-small lg:tw-text-dt-body-small"
                  :class="isECommerce ? 'tw-text-gray' : 'tw-text-dark-gray'"
                >
                  <QIcon name="calendar_today" size="16px" />
                  <span>{{ formatDate(item.publishedAt || item.createdAt) }}</span>
                </div>
                <div class="tw-flex tw-min-w-0 tw-flex-col" :class="isECommerce || isWebSphere ? 'tw-gap-2' : 'tw-gap-1'">
                  <p
                    v-if="isWebSphere"
                    class="author-text tw-truncate tw-text-[14px] tw-leading-[20px] tw-text-[#7bb7ef]"
                  >
                    {{ item.articleCategory?.title || '' }}
                  </p>
                  <div
                    v-if="isIndustrial"
                    class="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3 tw-text-[14px] tw-leading-[20px] tw-text-dark-gray"
                  >
                    <span class="author-text tw-truncate tw-text-secondary">
                      {{ item.articleCategory?.title || '' }}
                    </span>
                    <span class="tw-h-1 tw-w-1 tw-bg-light-gray-2" />
                    <span>{{ formatDate(item.publishedAt || item.createdAt) }}</span>
                    <span class="tw-h-1 tw-w-1 tw-bg-light-gray-2" />
                    <span class="tw-flex tw-items-center tw-gap-1">
                      <QIcon name="visibility" size="20px" />
                      ผู้เข้าชม {{ item.view || 0 }}
                    </span>
                  </div>
                  <h2
                    class="author-text tw-line-clamp-2 tw-text-black"
                    :class="articleTitleClass"
                  >
                    {{ item.title }}
                  </h2>
                  <p
                    v-if="!isWebSphere && !isIndustrial"
                    class="author-text tw-truncate tw-text-mb-body-small tw-text-secondary lg:tw-text-dt-body-small"
                  >
                    {{ item.articleCategory?.title || '' }}
                  </p>
                  <p
                    class="author-text tw-line-clamp-2 tw-text-dark-gray"
                    :class="isWebSphere ? 'tw-text-[16px] tw-leading-[24px] tw-text-[#808080]' : 'tw-text-mb-body-small lg:tw-text-dt-body-small'"
                  >
                    {{ item.shortDescription }}
                  </p>
                  <span
                    v-if="isIndustrial"
                    class="tw-mt-3 tw-hidden tw-w-fit tw-items-center tw-gap-1 tw-text-[16px] tw-font-medium tw-leading-[24px] tw-text-black hover:tw-text-secondary lg:tw-inline-flex"
                  >
                    {{ $t('global.readMore') }}
                    <QIcon name="arrow_forward" size="16px" class="tw-text-secondary" />
                  </span>
                  <div
                    v-if="isWebSphere"
                    class="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-[14px] tw-leading-[20px] tw-text-[#808080]"
                  >
                    <span>{{ formatDate(item.publishedAt || item.createdAt) }}</span>
                    <span class="tw-h-4 tw-w-px tw-bg-light-gray-2" />
                    <span class="tw-flex tw-items-center tw-gap-1">
                      <QIcon name="visibility" size="16px" />
                      ผู้เข้าชม {{ item.view || 0 }}
                    </span>
                  </div>
                </div>
              </div>
            </NuxtLink>
          </article>
          <Pagination
            v-if="!isWebSphere"
            v-model="paginationPage"
            class="tw-col-span-full"
            :total="total"
            :per-page="perPage"
          />
          <ThemeWebSphereButtonPagination
            v-else
            v-model="paginationPage"
            :total="total"
            :per-page="perPage"
          />
        </div>
        <LandingNoData v-else />
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
const {
  author,
  authorName,
  image,
  socialLinks,
  articles,
  breadcrumbs,
  paginationPage,
  perPage,
  total,
  getPath,
} = await useAuthorPage()

const { $shortDateFormat } = useNuxtApp()
const themeStyleStore = useThemeStyleStore()
const imageLoadFailed = ref(false)
const isECommerce = computed(() => themeStyleStore.frame === 'ECommerce')
const isWebSphere = computed(() => themeStyleStore.frame === 'WebSphere')
const isIndustrial = computed(() => themeStyleStore.frame === 'IndustrialService')
const isDefaultLayout = computed(() => !isECommerce.value && !isWebSphere.value && !isIndustrial.value)
const authorImage = computed(() => image.value?.medium || image.value?.large || image.value?.small || image.value?.tiny || '')
const initial = computed(() => authorName.value.trim().charAt(0).toUpperCase())
const formatDate = (date: string) => date ? ($shortDateFormat(date) as string) : ''
const mainClass = computed(() => {
  if (isECommerce.value) return 'tw-pb-8 lg:tw-pb-10'
  if (isWebSphere.value) return 'tw-pt-5 lg:tw-max-w-[1024px] lg:tw-px-0 lg:tw-pt-4 lg:tw-pb-8'
  if (isIndustrial.value) return 'tw-pb-8 tw-pt-4 lg:tw-pb-11 lg:tw-pt-0'
  return 'tw-py-8 lg:tw-py-11'
})
const profileSectionClass = computed(() => {
  if (isWebSphere.value) return 'tw-flex-col tw-items-center tw-gap-4 lg:tw-flex-row lg:tw-gap-[26px] lg:tw-px-[174px]'
  if (isIndustrial.value) return 'tw-flex-col tw-items-center tw-gap-6 tw-border-b tw-border-light-gray-2 tw-pb-10 lg:tw-px-[220px]'
  return 'tw-flex-col tw-items-center ' + (isECommerce.value ? 'tw-gap-4' : 'tw-gap-5')
})
const authorAvatarClass = computed(() => {
  if (isECommerce.value) return 'tw-h-[120px] tw-w-[120px] lg:tw-h-[200px] lg:tw-w-[200px]'
  if (isIndustrial.value) return 'tw-h-[120px] tw-w-[120px] lg:tw-h-[150px] lg:tw-w-[150px]'
  if (isDefaultLayout.value) return 'tw-h-[150px] tw-w-[150px] lg:tw-h-[200px] lg:tw-w-[200px]'
  return 'tw-h-[150px] tw-w-[150px]'
})
const profileTextClass = computed(() => {
  if (isWebSphere.value) return 'tw-text-center lg:tw-text-left'
  if (isIndustrial.value) return 'tw-text-center'
  if (isECommerce.value) return 'tw-max-w-[500px] tw-text-center'
  return 'tw-max-w-[700px] tw-border-b tw-border-light-gray-2 tw-pb-5 tw-text-center'
})
const authorNameClass = computed(() => {
  if (isECommerce.value) return 'tw-text-[24px] tw-font-medium tw-leading-[36px] lg:tw-text-[26px] lg:tw-leading-[40px]'
  if (isWebSphere.value) return 'tw-text-[20px] tw-font-semibold tw-leading-[30px] lg:tw-text-[24px] lg:tw-leading-[36px]'
  if (isIndustrial.value) return 'tw-text-[24px] tw-font-medium tw-leading-[36px]'
  return 'tw-text-mb-title-1 lg:tw-text-dt-title-1'
})
const authorBioClass = computed(() => {
  if (isIndustrial.value) return 'tw-mt-1 tw-line-clamp-2 tw-text-[16px] tw-leading-[24px]'
  return 'tw-mt-1 tw-text-mb-body lg:tw-text-dt-body'
})
const socialListClass = computed(() => {
  if (isECommerce.value) return 'tw-mt-4 tw-justify-center tw-gap-[10px]'
  if (isWebSphere.value) return 'tw-mt-4 tw-justify-center tw-gap-3 lg:tw-justify-start'
  if (isIndustrial.value) return 'tw-mt-4 tw-justify-center tw-gap-4'
  return 'tw-justify-center tw-gap-4'
})
const socialIconClass = computed(() => {
  if (isECommerce.value) return 'tw-h-[50px] tw-w-[50px] tw-rounded-full tw-border tw-border-light-gray-2'
  if (isWebSphere.value || isIndustrial.value) return 'tw-h-6 tw-w-6'
  return 'tw-h-8 tw-w-8'
})
const socialIconSize = computed(() => isWebSphere.value || isIndustrial.value ? '20px' : isECommerce.value ? '22px' : '24px')
const articleSectionClass = computed(() => {
  if (isECommerce.value) return 'tw-mt-8 lg:tw-mt-11'
  if (isWebSphere.value) return 'tw-mt-10 lg:tw-mt-14 lg:tw-px-[174px]'
  if (isIndustrial.value) return 'tw-mt-8 lg:tw-mt-10'
  return 'tw-mt-8 lg:tw-mt-14'
})
const articleListClass = computed(() => {
  if (isECommerce.value) return 'tw-mx-auto tw-grid tw-w-full tw-max-w-[822px] tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3 lg:tw-gap-x-4 lg:tw-gap-y-6'
  if (isWebSphere.value) return 'tw-flex tw-w-full tw-flex-col tw-gap-8'
  if (isIndustrial.value) return 'tw-flex tw-w-full tw-flex-col tw-gap-8 lg:tw-gap-10'
  return 'tw-flex tw-flex-col tw-gap-6'
})
const articleCardClass = computed(() => {
  if (isECommerce.value) return 'tw-rounded-lg ecommerce-author-card tw-border tw-border-light-gray-1'
  if (isWebSphere.value || isIndustrial.value) return ''
  return 'tw-rounded-lg author-card'
})
const articleLinkClass = computed(() => {
  if (isWebSphere.value) return 'tw-gap-2 lg:tw-grid-cols-[321px_minmax(0,1fr)] lg:tw-gap-8 lg:tw-items-center'
  if (isIndustrial.value) return 'tw-gap-3 lg:tw-grid-cols-[389px_minmax(0,1fr)] lg:tw-gap-8 lg:tw-items-start'
  if (!isECommerce.value) return 'lg:tw-grid-cols-[505px_minmax(0,1fr)]'
  return ''
})
const articleImageClass = computed(() => {
  if (isECommerce.value) return 'tw-aspect-[397/211] tw-w-full tw-object-cover tw-rounded-t-lg'
  if (isWebSphere.value) return 'tw-aspect-[343/192] tw-w-full tw-object-cover lg:tw-aspect-auto lg:tw-h-[179px]'
  if (isIndustrial.value) return 'tw-aspect-[343/193] tw-w-full tw-object-cover lg:tw-aspect-auto lg:tw-h-[219px]'
  return 'tw-aspect-[397/211] tw-w-full tw-object-cover lg:tw-aspect-auto lg:tw-h-[266px] lg:tw-rounded-l-lg'
})
const articleContentClass = computed(() => {
  if (isECommerce.value) return 'tw-gap-4 tw-p-4'
  if (isWebSphere.value) return 'tw-gap-2 tw-p-0'
  if (isIndustrial.value) return 'tw-gap-3 tw-p-0 lg:tw-gap-5'
  return 'tw-gap-2 tw-p-5 lg:tw-p-8'
})
const articleTitleClass = computed(() => {
  if (isECommerce.value) return 'tw-text-mb-title-4 lg:tw-text-dt-title-5'
  if (isWebSphere.value) return 'tw-text-[18px] tw-font-semibold tw-leading-[28px]'
  if (isIndustrial.value) return 'tw-text-[16px] tw-font-medium tw-leading-[24px] lg:tw-text-[18px] lg:tw-leading-[27px]'
  return 'tw-text-mb-title-4 lg:tw-text-dt-title-4'
})

watch(authorImage, () => {
  imageLoadFailed.value = false
})
</script>

<style scoped>
.author-card {
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);
}

.ecommerce-author-card {
  box-shadow: 0 0 12px rgba(40, 40, 40, 0.04);
}

.author-text {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
